import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Pagination from '../../components/Pagination';

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface LicenseEvent {
  id: number;
  customer_id: string;
  customer_name: string;
  event_type: string;
  message: string | null;
  days_until_expiry: number | null;
  license_expiration: string | null;
  source_ip: string | null;
  created_at: string | null;
  acknowledged: boolean;
  acknowledged_at: string | null;
}

export default function EventsPage() {
  const [events, setEvents] = useState<LicenseEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, eventTypeFilter, searchTerm]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/license-events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeEvent = async (eventId: number) => {
    try {
      await axios.post(`${API_URL}/admin/license-events/${eventId}/acknowledge`);
      fetchEvents();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to acknowledge event');
    }
  };

  const acknowledgeAll = async () => {
    const unreadEvents = events.filter(e => !e.acknowledged);
    for (const event of unreadEvents) {
      try {
        await axios.post(`${API_URL}/admin/license-events/${event.id}/acknowledge`);
      } catch (error) {
        console.error('Error acknowledging event:', event.id);
      }
    }
    fetchEvents();
  };

  const getEventTypeStyle = (eventType: string): { bg: string; text: string; icon: string; borderColor: string } => {
    switch (eventType) {
      case 'LICENSE_EXPIRING_SOON':
        return { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '⚠️', borderColor: 'border-yellow-400' };
      case 'GRACE_PERIOD_WARNING':
        return { bg: 'bg-orange-100', text: 'text-orange-800', icon: '⏰', borderColor: 'border-orange-400' };
      case 'GRACE_PERIOD_EXPIRED':
        return { bg: 'bg-red-100', text: 'text-red-800', icon: '🚨', borderColor: 'border-red-400' };
      case 'LICENSE_INVALID':
        return { bg: 'bg-red-100', text: 'text-red-800', icon: '❌', borderColor: 'border-red-500' };
      case 'PLATFORM_SHUTDOWN':
        return { bg: 'bg-red-200', text: 'text-red-900', icon: '🛑', borderColor: 'border-red-600' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800', icon: '📋', borderColor: 'border-gray-400' };
    }
  };

  const eventTypes = Array.from(new Set(events.map(e => e.event_type)));

  const getFilteredEvents = () => {
    let filtered = events;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(event =>
        event.customer_name.toLowerCase().includes(term) ||
        event.customer_id.toLowerCase().includes(term) ||
        (event.message && event.message.toLowerCase().includes(term)) ||
        (event.source_ip && event.source_ip.includes(term))
      );
    }

    // Read status filter
    if (filter === 'unread') {
      filtered = filtered.filter(e => !e.acknowledged);
    } else if (filter === 'read') {
      filtered = filtered.filter(e => e.acknowledged);
    }

    // Event type filter
    if (eventTypeFilter !== 'all') {
      filtered = filtered.filter(e => e.event_type === eventTypeFilter);
    }

    return filtered;
  };

  const filteredEvents = getFilteredEvents();
  const paginatedEvents = filteredEvents.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const unreadCount = events.filter(e => !e.acknowledged).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">License Events</h1>
          <p className="text-gray-600">Webhook notifications from customer deployments</p>
        </div>
        <div className="flex items-center gap-4">
          {unreadCount > 0 && (
            <button
              onClick={acknowledgeAll}
              className="text-sm text-blue-600 hover:underline"
            >
              Mark all as read
            </button>
          )}
          <button
            onClick={fetchEvents}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Total Events</div>
          <div className="text-2xl font-bold text-gray-900">{events.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Unread</div>
          <div className="text-2xl font-bold text-red-600">{unreadCount}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Critical</div>
          <div className="text-2xl font-bold text-red-600">
            {events.filter(e => ['PLATFORM_SHUTDOWN', 'LICENSE_INVALID', 'GRACE_PERIOD_EXPIRED'].includes(e.event_type)).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Warnings</div>
          <div className="text-2xl font-bold text-yellow-600">
            {events.filter(e => ['LICENSE_EXPIRING_SOON', 'GRACE_PERIOD_WARNING'].includes(e.event_type)).length}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Search by customer, message, or IP..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Status:</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | 'unread' | 'read')}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Type:</span>
          <select
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            {eventTypes.map(type => (
              <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
        <span className="text-sm text-gray-400">
          {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Events List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {paginatedEvents.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <p className="text-lg font-medium">No events found</p>
            <p className="text-sm mt-1">
              {searchTerm || filter !== 'all' || eventTypeFilter !== 'all'
                ? 'Try adjusting your filters.'
                : 'Events will appear here when customer deployments send webhook notifications.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {paginatedEvents.map(event => {
              const style = getEventTypeStyle(event.event_type);
              return (
                <div
                  key={event.id}
                  className={`px-6 py-4 ${event.acknowledged ? 'bg-gray-50' : 'bg-white'} border-l-4 ${style.borderColor}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <span className="text-2xl">{style.icon}</span>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${style.bg} ${style.text}`}>
                            {event.event_type.replace(/_/g, ' ')}
                          </span>
                          {!event.acknowledged && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500 text-white">New</span>
                          )}
                        </div>
                        <p className="font-medium text-gray-900">
                          {event.customer_name}
                        </p>
                        <p className="text-sm text-gray-500">{event.customer_id}</p>
                        {event.message && (
                          <p className="text-sm text-gray-600 mt-2">{event.message}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>
                            {event.created_at ? new Date(event.created_at).toLocaleString() : 'Unknown time'}
                          </span>
                          {event.days_until_expiry !== null && (
                            <span className={event.days_until_expiry < 0 ? 'text-red-600 font-medium' : ''}>
                              {event.days_until_expiry < 0
                                ? `Expired ${Math.abs(event.days_until_expiry)} days ago`
                                : `${event.days_until_expiry} days until expiry`}
                            </span>
                          )}
                          {event.source_ip && (
                            <span>IP: {event.source_ip}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!event.acknowledged ? (
                        <button
                          onClick={() => acknowledgeEvent(event.id)}
                          className="text-sm px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Acknowledge
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">
                          Acknowledged {event.acknowledged_at ? new Date(event.acknowledged_at).toLocaleDateString() : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Pagination
          currentPage={currentPage}
          totalItems={filteredEvents.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>
    </div>
  );
}
